import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

export function StockAdjustmentDialog({ product, onClose, onDone }) {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [current, setCurrent] = useState(null);
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => {
      const list = (r.data || []).filter((b) => b.type === "warehouse" || b.type === "retail_showroom");
      setBranches(list);
      const wh = list.find((b) => b.type === "warehouse");
      if (wh) setBranchId(wh.id);
    });
  }, []);

  useEffect(() => {
    if (!branchId || !product) return;
    const bd = (product.stock_breakdown || []).find((s) => s.branch_id === branchId);
    const c = bd ? Number(bd.quantity || 0) : (branches.find((b) => b.id === branchId)?.type === "warehouse" ? Number(product.warehouse_stock ?? 0) : 0);
    setCurrent(c);
    setQty(String(c));
  }, [branchId, product, branches]);

  const delta = qty === "" || current == null ? 0 : Number(qty) - current;

  const submit = async () => {
    if (!branchId) return toast.error("Pick a location");
    if (!reason.trim()) return toast.error("A reason is required");
    setSaving(true);
    try {
      const r = await api.post(`/products/${product.id}/stock-adjustment`, { branch_id: branchId, new_quantity: Number(qty), reason: reason.trim() });
      toast.success(`${product.name}: ${r.data.old_quantity} → ${r.data.new_quantity} at ${r.data.branch}${r.data.value ? ` (₹${r.data.value} posted)` : ""}`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjustment failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="stock-adjustment-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-[#C05621]" /> Adjust Stock — {product?.name}</DialogTitle>
          <DialogDescription>Super Admin only. Sets the on-hand quantity at one location; writes an adjustment movement and a Stock ↔ Waste &amp; Damage ledger entry, fully audited.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Location</Label>
            <select className="w-full h-10 border border-[#EAE5D9] rounded-md px-3 text-sm bg-white" value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="stock-adjust-branch">
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.type === "warehouse" ? " (HO Warehouse)" : ""}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Current quantity</Label>
              <Input value={current ?? "—"} readOnly className="bg-[#F7F2E8]" data-testid="stock-adjust-current" />
            </div>
            <div>
              <Label>New quantity *</Label>
              <Input type="number" min="0" step="0.001" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="stock-adjust-new-qty" />
            </div>
          </div>
          {delta !== 0 && (
            <div className={`text-xs font-medium ${delta > 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="stock-adjust-delta">
              {delta > 0 ? "+" : ""}{delta} {product?.unit} · valued at HO rate ₹{Number(product?.ho_transfer_cost || product?.sale_price || 0).toFixed(2)} = ₹{(Math.abs(delta) * Number(product?.ho_transfer_cost || product?.sale_price || 0)).toFixed(2)}
            </div>
          )}
          <div>
            <Label>Reason *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Physical count 31-Mar, damaged in storage, opening stock" data-testid="stock-adjust-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || delta === 0 || !reason.trim()} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="stock-adjust-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post adjustment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
